// Shared competition presentation helpers; backend enforcement remains authoritative.
export function registrationDeadline(comp) {
  // Existing rule: one hour before the start matchday. A single-league kickoff
  // cannot establish a mapped multi-league round's deadline.
  return comp.is_multi_league ? NaN : (comp.first_kickoff_time ? new Date(comp.first_kickoff_time).getTime() : NaN) - 60 * 60 * 1000;
}

export function getCompetitionRegistration(comp = {}, now = Date.now()) {
  const deadline = registrationDeadline(comp);
  const state = comp.registration_status === 'closed' ||
    (comp.registration_status === 'open' && Number.isFinite(deadline) && now >= deadline)
    ? 'closed' : comp.registration_status === 'open' ? 'open' : 'unavailable';
  // Lifecycle is separate from registration; finished games still have details.
  return {state, deadline, canRegister:state === 'open' && !['inactive','ended','completed','cancelled'].includes(comp.status)};
}

export const getCompetitionTypeDisplay = (type) => {
  switch (type) {
    case 'season':
      return 'Season';
    case 'knockout':
      return 'Knockout';
    case 'scout':
      return 'Scout';
    case 'world_stage':
      return 'World Stage';
    default:
      return type; // fallback to the raw type
  }
};

// ─── League colour palette ──────────────────────────────────────────────────
// Keys match the `league` string stored in the competitions table.
// Each entry has `bg` (dominant brand dark) and `accent` (highlight).
export const LEAGUE_COLORS = {
  // England
  'Premier League':      { bg: '#38003C', accent: '#00FF85' },
  'EFL Championship':    { bg: '#001E62', accent: '#0099CC' },
  'EFL League One':      { bg: '#001E62', accent: '#0099CC' },
  'EFL League Two':      { bg: '#001E62', accent: '#0099CC' },
  // Germany
  'Bundesliga':          { bg: '#8B0000', accent: '#E32221' },
  '2. Bundesliga':       { bg: '#8B0000', accent: '#E32221' },
  // Spain
  'La Liga':             { bg: '#0A1D47', accent: '#EF0303' },
  'La Liga 2':           { bg: '#0A1D47', accent: '#EF0303' },
  // Italy
  'Serie A':             { bg: '#00205B', accent: '#009246' },
  'Serie B':             { bg: '#00205B', accent: '#009246' },
  // France
  'Ligue 1':             { bg: '#0A2D5C', accent: '#0055A4' },
  'Ligue 2':             { bg: '#0A2D5C', accent: '#0055A4' },
  // Netherlands
  'Eredivisie':          { bg: '#B30000', accent: '#FF4500' },
  // Portugal
  'Primeira Liga':       { bg: '#004A1E', accent: '#006400' },
  // USA
  'Major League Soccer': { bg: '#001E60', accent: '#C8102E' },
  'MLS':                 { bg: '#001E60', accent: '#C8102E' },
  // Scotland
  'Scottish Premiership':{ bg: '#003399', accent: '#CC0000' },
  // World competitions
  'FIFA World Cup':      { bg: '#3D2B00', accent: '#DAA520' },
  'World Cup':           { bg: '#3D2B00', accent: '#DAA520' },
  'UEFA Champions League':{ bg: '#001489', accent: '#00B4FF' },
  'UEFA Europa League':  { bg: '#1A1A1A', accent: '#F37021' },
};

// Country/federation flag emoji per league
export const LEAGUE_FLAGS = {
  'Premier League':       '🏴󠁧󠁢󠁥󠁮󠁧󠁿',
  'EFL Championship':     '🏴󠁧󠁢󠁥󠁮󠁧󠁿',
  'EFL League One':       '🏴󠁧󠁢󠁥󠁮󠁧󠁿',
  'EFL League Two':       '🏴󠁧󠁢󠁥󠁮󠁧󠁿',
  'Bundesliga':           '🇩🇪',
  '2. Bundesliga':        '🇩🇪',
  'La Liga':              '🇪🇸',
  'La Liga 2':            '🇪🇸',
  'Serie A':              '🇮🇹',
  'Serie B':              '🇮🇹',
  'Ligue 1':              '🇫🇷',
  'Ligue 2':              '🇫🇷',
  'Eredivisie':           '🇳🇱',
  'Primeira Liga':        '🇵🇹',
  'Major League Soccer':  '🇺🇸',
  'MLS':                  '🇺🇸',
  'Scottish Premiership': '🏴󠁧󠁢󠁳󠁣󠁴󠁿',
  'FIFA World Cup':       '🌍',
  'World Cup':            '🌍',
  'UEFA Champions League':'🌍',
  'UEFA Europa League':   '🌍',
};

// Competition type visual config
export const TYPE_CONFIG = {
  knockout:    { label: 'Knockout',    color: '#BA6830' },
  season:      { label: 'Season',      color: '#2563EB' },
  world_stage: { label: 'World Stage', color: '#7C3AED' },
  scout:       { label: 'Scout',       color: '#16A34A' },
};

// Place your FIFA 2026 image in `public/world-cup-2026-mark.webp`.
export const WORLD_CUP_2026_IMAGE_PATH = '/world-cup-2026-mark.webp';

/** Returns { bg, accent } for a league, or a neutral dark fallback. */
export function getLeagueColors(league) {
  return LEAGUE_COLORS[league] || { bg: '#1F2937', accent: '#4B5563' };
}

/** Returns the flag emoji for a league, or a generic globe. */
export function getLeagueFlag(league) {
  return LEAGUE_FLAGS[league] || '🌐';
}

/** Returns the TYPE_CONFIG entry for a competition type. */
export function getTypeConfig(type) {
  return TYPE_CONFIG[type] || { label: type || 'Unknown', color: '#6B7280' };
}

export function isWorldCupCompetition(comp) {
  if (!comp) return false;
  const league = String(comp.league || '').toLowerCase();
  return comp.type === 'world_stage' || league === 'fifa world cup' || league === 'world cup';
}

/**
 * Returns visual assets for competition cards.
 * For World Cup competitions, forces a custom image for both badge and flag slots.
 */
export function getCompetitionBrandAssets(comp) {
  if (isWorldCupCompetition(comp)) {
    return {
      badgeUrl: WORLD_CUP_2026_IMAGE_PATH,
      flagUrl: WORLD_CUP_2026_IMAGE_PATH,
    };
  }

  return {
    badgeUrl: comp?.league_logo_url || null,
    flagUrl: comp?.league_flag_url || null,
  };
}
